import { useTranslation } from 'react-i18next';
import type { TourDefinition } from '@klonup/design-system';

/**
 * Tour "Gestionar una conexión" para Admin. Recorre el detalle de una conexión enfocado en
 * lo que un Admin gestiona: moverse entre conexiones, ver tareas y equipo (invitando si
 * quiere) y abrir la configuración de la conexión.
 *
 * El paso `configure` usa `advanceOn`: avanza cuando el Admin pulsa de verdad "Configurar",
 * lo que abre la sidebar de ajustes (`connection-settings-sidebar`). El paso siguiente la resalta y
 * cierra el tour. La creación de tareas se enseña en un tour aparte (admin-create-task).
 *
 * Sin `requiredPath`: las anclas viven en la ruta dinámica `/connections/:id`. Se auto-dispara por
 * evento al entrar (ver ConnectionDetailsPage, role admin) y se deshabilita en el menú fuera del
 * detalle de conexión (ver OrgLayout) — mismo patrón que bm-connection-detail.
 */
export function useAdminConnectionDetailTour(): TourDefinition {
  const { t } = useTranslation();
  return {
    id: 'admin-connection-detail',
    title: t('onboarding.admin_connection_detail.menuTitle'),
    description: t('onboarding.admin_connection_detail.menuDescription'),
    icon: '🛠️',
    steps: [
      {
        id: 'intro',
        // Sin target → modal centrado de introducción.
        title: t('onboarding.admin_connection_detail.steps.intro.title'),
        description: t('onboarding.admin_connection_detail.steps.intro.description'),
      },
      {
        id: 'switcher',
        target: '[data-tour="connection-context-switcher"]',
        title: t('onboarding.admin_connection_detail.steps.switcher.title'),
        description: t('onboarding.admin_connection_detail.steps.switcher.description'),
      },
      {
        id: 'tasks',
        target: '[data-tour="connection-tasks"]',
        title: t('onboarding.admin_connection_detail.steps.tasks.title'),
        description: t('onboarding.admin_connection_detail.steps.tasks.description'),
      },
      {
        id: 'team',
        target: '[data-tour="connection-team"]',
        title: t('onboarding.admin_connection_detail.steps.team.title'),
        description: t('onboarding.admin_connection_detail.steps.team.description'),
      },
      {
        id: 'configure',
        target: '[data-tour="connection-configure-button"]',
        // Avanza cuando el Admin pulsa de verdad "Configurar" → abre la sidebar de ajustes.
        advanceOn: { on: 'click' },
        title: t('onboarding.admin_connection_detail.steps.configure.title'),
        description: t('onboarding.admin_connection_detail.steps.configure.description'),
      },
      {
        id: 'settings',
        target: '[data-tour="connection-settings-sidebar"]',
        title: t('onboarding.admin_connection_detail.steps.settings.title'),
        description: t('onboarding.admin_connection_detail.steps.settings.description'),
      },
    ],
  };
}
